<template>
  <b-overlay :show="loading" spinner-variant="primary" rounded="sm">
    <div class="apps-home">
      <div v-if="applications.length || recently_accessed_applications.length" class="apps-toolbar">
        <span class="apps-toolbar__label">View</span>
        <div class="view-toggle" role="group" aria-label="Layout view">
          <button
            type="button"
            class="view-toggle__btn"
            :class="{ 'is-active': viewMode === 'grid' }"
            aria-label="Grid view"
            @click="viewMode = 'grid'"
          >
            <feather-icon icon="GridIcon" size="15" />
          </button>
          <button
            type="button"
            class="view-toggle__btn"
            :class="{ 'is-active': viewMode === 'list' }"
            aria-label="List view"
            @click="viewMode = 'list'"
          >
            <feather-icon icon="ListIcon" size="15" />
          </button>
        </div>
      </div>

      <section v-if="recently_accessed_applications.length" class="apps-section">
        <h2 class="apps-section__title">Recently Used</h2>
        <div class="apps-grid" :class="{ 'apps-grid--list': viewMode === 'list' }">
          <div
            v-for="(application, index) of recently_accessed_applications"
            :key="`recent-${index}`"
            class="app-card card"
          >
            <div class="app-card__body card-body">
              <b-avatar
                size="56"
                class="app-card__avatar"
                :src="getValueFromSource(application, 'logo')"
                :text="sentenceCase(avatarText(`${application.title}`))"
                :variant="application.logo ? '' : 'light-primary'"
                square
              />
              <div class="app-card__info">
                <h3 v-b-tooltip.hover :title="application.title" class="app-card__title">
                  {{ application.title }}
                </h3>
                <span class="app-card__meta">
                  {{ formatDistance(new Date(application.last_seen), new Date(), { addSuffix: true }) }}
                </span>
              </div>
            </div>
            <b-button
              variant="primary"
              :block="viewMode === 'grid'"
              class="app-card__action"
              @click="openApplicationHandler(application)"
            >
              Open
            </b-button>
          </div>
        </div>
      </section>

      <section v-if="applications.length" class="apps-section">
        <h2 class="apps-section__title">Applications</h2>
        <div class="apps-grid" :class="{ 'apps-grid--list': viewMode === 'list' }">
          <div
            v-for="(application, index) of applications"
            :key="`app-${index}`"
            class="app-card card"
          >
            <div class="app-card__body card-body">
              <b-avatar
                size="56"
                class="app-card__avatar"
                :src="getValueFromSource(application, 'logo')"
                :text="sentenceCase(avatarText(`${application.title}`))"
                :variant="application.logo ? '' : 'light-primary'"
                square
              />
              <div class="app-card__info">
                <h3 v-b-tooltip.hover :title="application.title" class="app-card__title">
                  {{ application.title }}
                </h3>
              </div>
              <b-popover :target="`app-help-${application._id}`" triggers="hover" placement="top">
                <template #title>{{ application.title }}</template>
                {{ application.description || 'No description' }}
              </b-popover>
              <feather-icon
                :id="`app-help-${application._id}`"
                icon="HelpCircleIcon"
                size="15"
                class="app-card__help"
              />
            </div>
            <b-button
              variant="primary"
              :block="viewMode === 'grid'"
              class="app-card__action"
              @click="openApplicationHandler(application)"
            >
              Open
            </b-button>
          </div>
        </div>
      </section>
      <div v-else-if="!loading" class="apps-empty">
        <feather-icon icon="GridIcon" size="36" class="apps-empty__icon" />
        <h3>No applications found</h3>
        <p class="text-muted mb-0">You don't have access to any applications yet.</p>
      </div>
    </div>
    <b-modal
      id="add-or-update"
      v-model="showApplicationAuthorizationModal"
      size="sm"
      centered
      hide-footer
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      @hidden="() => { showApplicationAuthorizationModal = false }"
      @hide="() => { showApplicationAuthorizationModal = false }"
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header py-1 mb-2"
        style="border-bottom: 1px solid #000000"
      >
        <h5
          class="mb-0"
          style="font-weight: 400; font-size: 14px; line-height: 160%"
        >
          Authorize Application Access
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="() => { showApplicationAuthorizationModal = false }"
        />
      </div>
      <div class="text-center mt-1 mb-2">
        <p>An otp verification code has been sent to your number. Enter the code received below to proceed</p>
      </div>
      <validation-observer ref="authenticateForm" #default="{ invalid }">
        <b-form @submit.prevent="openApplication">
          <b-form-group label-for="code">
            <validation-provider
              #default="{ errors }"
              name="Code"
              vid="code"
              rules="required"
            >
              <div class="custom-input">
                <span class="leading">
                  <PasswordLock />
                </span>
                <input
                  v-model="code"
                  type="number"
                  class="otp"
                  placeholder="* * * * *"
                />
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div>
            <b-button
              class="py-1"
              type="submit"
              variant="primary"
              block
              :disabled="invalid"
            >
              Grant Access
            </b-button>
          </div>
        </b-form>
      </validation-observer>
      <div class="text-center pt-1">
        <div class="">
          <p v-if="canResend" class="font-weight-bold border border-bottom m-0 p-0 cursor-pointer" @click="resendRequest"> Resend Code
          </p>
        </div>

        <div v-if="!canResend">
          <p class="m-0 p-0">
            Resend verification code after <span class="text-success">{{ timerString }}</span>
          </p>
        </div>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import { differenceInSeconds, formatDistance } from "date-fns";
import { get } from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {
  BOverlay,
  BAvatar,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  VBTooltip,
  VBTooltipPlugin,
  BPopover
} from "bootstrap-vue";
import { required } from "@validations";
import PasswordLock from "@/assets/svg/PasswordLock.svg";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BButton,
    BAvatar,
    BOverlay,
    BModal,
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    PasswordLock,
    BPopover
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-tooltip-plugin': VBTooltipPlugin
  },
  data() {
    return {
      code: "",
      loading: false,
      applications: [],
      recently_accessed_applications: [],
      showApplicationAuthorizationModal: false,
      currentApplication: null,
      lastCodeRequest: null,
      timerString: '00:00',
      formErrorMessage: "",
      required,
      interval: null,
      viewMode: localStorage.getItem('pac_home_view_mode') || 'grid',
    };
  },
  computed: {
    canResend() {
      return this.timerString === "00:00";
    }
  },
  watch: {
    showApplicationAuthorizationModal: {
      handler() {
        this.code = ""
      }
    },
    viewMode(value) {
      localStorage.setItem('pac_home_view_mode', value);
    }
  },
  created() {
    this.fetchApplications();
  },
  methods: {
    async fetchApplications() {
      try {
        this.loading = true;

        const request =
          await this.useJwt().mainService.users.applications.list();
        const { data } = request.data;

        this.applications = data.applications;
        this.recently_accessed_applications = data.recently_accessed_applications;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async openApplication() {
      try {
        if (!this.currentApplication) {
          return;
        }
        this.loading = true;
        const application = this.currentApplication;

        const request = await this.useJwt().mainService.users.applications.open(
          application._id,
          { code: this.code }
        );
        const { data } = request.data;

        this.currentApplication = null;
        this.showApplicationAuthorizationModal = false;
        window.open(data.redirect_url, "_blank");
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.formErrorMessage = error_message
        if (error_message.includes("Too many attempts")) {
          this.showApplicationAuthorizationModal = false;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async requestAccess(application) {
      try {
        this.loading = true;

        const response = await this.useJwt().mainService.users.applications.requestAccess(
          application._id,
        );

        const { data } = response.data;

        this.currentApplication = application;
        this.showApplicationAuthorizationModal = true;

        if (this.interval) {
          clearInterval(this.interval);
        }

        this.lastCodeRequest = new Date(data.expires_in)
        this.startTimer(differenceInSeconds(this.lastCodeRequest, new Date()));

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success!",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "Verification code sent to your number.",
          },
        });
      } catch (error) {
        const error_message =
        get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    resendRequest() {
      if (!this.currentApplication) { return }
      if (this.currentApplication.access_type === "external") {
        window.open(this.currentApplication.full_url, "_blank");
        return
      }
      this.requestAccess(this.currentApplication)
    },
    startTimer(duration) {
      let timer = duration;
      let minutes;
      let seconds;

      this.interval = setInterval(() => {
        minutes = parseInt(timer / 60, 10);
        seconds = parseInt(timer % 60, 10);

        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;

        // eslint-disable-next-line no-plusplus
        if (--timer < 0) {
          clearInterval(this.interval);
        }

        this.timerString = minutes + ":" + seconds;
      }, 1000);
    },
    openApplicationHandler(application) {
      this.currentApplication = application;

      if (application.access_type === "external") {
        window.open(application.full_url, "_blank");
        return
      }

      if (!application.require_otp) {
        this.openApplication();
      } else {
        this.requestAccess(application);
      }
    },
    formatDistance
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/dashboard-ecommerce.scss";
@import "@core/scss/vue/libs/chart-apex.scss";
</style>

<style scoped>
.apps-home {
  padding-bottom: 0.5rem;
}

.apps-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.apps-toolbar__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.55;
}

.view-toggle {
  display: inline-flex;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.4rem;
  overflow: hidden;
}

.view-toggle__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 1.85rem;
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.55;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.view-toggle__btn + .view-toggle__btn {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.view-toggle__btn:hover {
  opacity: 0.85;
}

.view-toggle__btn.is-active {
  background: rgba(0, 0, 0, 0.06);
  opacity: 1;
}

.apps-section {
  margin-bottom: 2.25rem;
}

.apps-section__title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.55;
  margin-bottom: 1rem;
}

.apps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
}

.app-card {
  margin-bottom: 0;
  border-radius: 0.6rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.app-card:hover {
  border-color: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.06);
}

.app-card__body {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 1.4rem 1rem;
  position: relative;
}

.app-card__avatar {
  flex-shrink: 0;
  border-radius: 0.6rem;
  overflow: hidden;
}

.app-card__info {
  min-width: 0;
  flex: 1;
}

.app-card__title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-card__meta {
  font-size: 0.8rem;
  opacity: 0.55;
}

.app-card__help {
  position: absolute;
  top: 1.3rem;
  right: 1.3rem;
  opacity: 0.45;
  cursor: pointer;
}

.app-card__action {
  margin: 0 1.4rem 1.3rem;
  width: calc(100% - 2.8rem);
  border-radius: 0.4rem;
  font-weight: 500;
}

/* List view */
.apps-grid--list {
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.apps-grid--list .app-card {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.apps-grid--list .app-card__body {
  padding: 0.85rem 1.2rem;
  flex: 1;
}

.apps-grid--list .app-card__help {
  position: static;
  margin-left: 0.5rem;
}

.apps-grid--list .app-card__action {
  margin: 0 1.2rem;
  width: auto;
  flex-shrink: 0;
}

.apps-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 1rem;
}

.apps-empty__icon {
  opacity: 0.35;
  margin-bottom: 1rem;
}

@media (max-width: 480px) {
  .apps-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
  }

  .app-card__body {
    padding: 1rem 1.1rem 0.75rem;
    gap: 0.75rem;
  }

  .app-card__action {
    margin: 0 1.1rem 1rem;
    width: calc(100% - 2.2rem);
  }

  .apps-grid--list .app-card {
    flex-wrap: wrap;
  }

  .apps-grid--list .app-card__action {
    margin: 0 1.1rem 0.9rem;
    width: calc(100% - 2.2rem);
  }
}
</style>

<style lang="scss">
@import '@core/scss/base/pages/app-general.scss';
@import '@core/scss/vue/pages/page-auth.scss';
</style>
